/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useMemo, useState } from 'react';
import StatusModal from '@/components/feedback/StatusModal';
import ConfigurationDataEditor from '@/components/tabs/demo/ConfigurationDataEditor';
import {
    mergeSystemConfigurationDefaults,
    SYSTEM_CONFIGURATION_SCHEMA,
    type SystemConfiguration,
} from '@/config/systemConfigurationSchema';
import { EeCheckApiError, eeCheckApi } from '@/api/eeCheckApi';
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';
import { isExpiredSessionError } from '@/lib/app/debugRequests';
import { JsonConfiguration } from '@/lib/configuration/jsonConfiguration';
import {
    getConfigurationCustomers,
    getConfigurationSystems,
    type ConfigurationCustomer,
} from '@/lib/configuration/configurationSystems';

const systemConfiguration = new JsonConfiguration<SystemConfiguration>(SYSTEM_CONFIGURATION_SCHEMA);

function formatConfigurationData(data: unknown, systemName: string): string {
    return JSON.stringify(mergeSystemConfigurationDefaults(data, systemName), null, 4);
}

function getEditorConfiguration(content: string, systemName: string): SystemConfiguration | null {
    try {
        const configuration = mergeSystemConfigurationDefaults(JSON.parse(content), systemName);
        const validation = systemConfiguration.validate(configuration);

        return validation.isValid ? validation.data : null;
    } catch {
        return null;
    }
}

function ConfigurationTab() {
    const { t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired, username } = useSession();
    const [customer, setCustomer] = useState<ConfigurationCustomer>(() => getConfigurationCustomers()[0] ?? 'muenster');
    const systems = useMemo(() => getConfigurationSystems(customer), [customer]);
    const [selectedSystem, setSelectedSystem] = useState(() => getConfigurationSystems(
        getConfigurationCustomers()[0] ?? 'muenster',
    )[0] ?? '');
    const [content, setContent] = useState('{}');
    const [isReading, setIsReading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [modalState, setModalState] = useState({
        isOpen: false,
        message: '',
        title: '',
    });

    const isRequesting = isReading || isSaving;
    const editorConfiguration = useMemo(
        () => getEditorConfiguration(content, selectedSystem),
        [content, selectedSystem],
    );

    function handleCustomerChange(nextCustomer: ConfigurationCustomer) {
        const nextSystems = getConfigurationSystems(nextCustomer);

        setCustomer(nextCustomer);
        setSelectedSystem(nextSystems[0] ?? '');
        setContent('{}');
    }

    function handleSystemChange(nextSystem: string) {
        setSelectedSystem(nextSystem);
        setContent('{}');
    }

    function handleEditorChange(configuration: SystemConfiguration) {
        setContent(JSON.stringify(configuration, null, 4));
    }

    function showModal(title: string, message: string) {
        setModalState({ isOpen: true, message, title });
    }

    function handleRequestError(error: unknown) {
        if (isExpiredSessionError(error)) {
            onSessionExpired();
            return;
        }

        const message = error instanceof EeCheckApiError || error instanceof Error
            ? error.message
            : t('Unknown error.');

        showModal(t('Configuration'), t('Configuration request failed: {message}', { message }));
    }

    async function handleRead() {
        if (!accessToken || isSessionExpired) {
            return;
        }

        setIsReading(true);

        try {
            const { data } = await eeCheckApi.readConfiguration(accessToken, {
                customer,
                system: selectedSystem,
            });
            const configurationData = data.success && data.data !== null
                ? data.data.data
                : {};

            setContent(formatConfigurationData(configurationData, selectedSystem));
            showModal(
                t('Configuration'),
                data.success
                    ? t('Configuration data was loaded.')
                    : t('No configuration data exists for the selected customer and system.'),
            );
        } catch (error) {
            handleRequestError(error);
        } finally {
            setIsReading(false);
        }
    }

    async function handleSave() {
        if (!accessToken || isSessionExpired) {
            return;
        }

        let parsedContent: unknown;

        try {
            parsedContent = JSON.parse(content);
        } catch {
            showModal(t('Configuration'), t('Configuration data must contain valid JSON.'));
            return;
        }

        if (!systemConfiguration.validate(parsedContent).isValid) {
            showModal(t('Configuration'), t('Configuration data does not match the required schema.'));
            return;
        }

        setIsSaving(true);

        try {
            const { data } = await eeCheckApi.saveConfiguration(
                accessToken,
                { customer, system: selectedSystem },
                JSON.stringify(parsedContent),
            );

            showModal(
                t('Configuration'),
                data.success
                    ? t('Configuration data was saved.')
                    : t('Configuration data could not be saved.'),
            );
        } catch (error) {
            handleRequestError(error);
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <div className="flex w-full flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('Configuration Demo')}</h2>
                <p>{t('Read, edit, and save JSON configuration data for a customer and system.')}</p>
            </section>

            <section className="dashboard-panel flex items-stretch justify-start p-5">
                <div className="grid w-full grid-cols-1 gap-y-4 text-left md:grid-cols-2 md:divide-x md:divide-base-content">
                    <div className="flex min-h-12 items-center gap-3 md:pr-5">
                        <label className="text-sm font-semibold" htmlFor="configuration-customer">
                            {t('Customer')}
                        </label>
                        <select
                            id="configuration-customer"
                            className="select select-bordered w-auto"
                            value={customer}
                            onChange={(event) => handleCustomerChange(event.target.value as ConfigurationCustomer)}
                            disabled={isRequesting}
                        >
                            {getConfigurationCustomers().includes('neutraubling') ? (
                                <option value="neutraubling">{t('Neutraubling')}</option>
                            ) : null}
                            <option value="muenster">{t('Münster')}</option>
                        </select>
                    </div>

                    <div className="flex min-h-12 items-center gap-3 md:pl-5">
                        <label className="text-sm font-semibold" htmlFor="configuration-system">
                            {t('System')}
                        </label>
                        <select
                            id="configuration-system"
                            className="select select-bordered w-auto"
                            value={selectedSystem}
                            onChange={(event) => handleSystemChange(event.target.value)}
                            disabled={isRequesting}
                        >
                            {systems.map((system) => (
                                <option key={system} value={system}>{system}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </section>

            <section className="flex flex-wrap gap-3">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void handleRead()}
                    disabled={!accessToken || isSessionExpired || isRequesting || !selectedSystem}
                >
                    {isReading ? t('Reading configuration data...') : t('Read data')}
                </button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => void handleSave()}
                    disabled={!accessToken || isSessionExpired || isRequesting || !selectedSystem}
                >
                    {isSaving ? t('Saving configuration data...') : t('Save data')}
                </button>
            </section>

            <section className="dashboard-panel flex items-stretch justify-start p-5">
                {editorConfiguration ? (
                    <ConfigurationDataEditor
                        configuration={editorConfiguration}
                        disabled={isRequesting}
                        onChange={handleEditorChange}
                        username={username}
                    />
                ) : (
                    <p className="text-sm text-error" role="alert">
                        {t('The JSON configuration must be valid to use the editor.')}
                    </p>
                )}
            </section>

            <section className="dashboard-panel flex items-stretch justify-start p-5">
                <div className="flex w-full flex-col gap-2">
                    <label className="text-sm font-semibold" htmlFor="configuration-content">
                        {t('Configuration data JSON')}
                    </label>
                    <textarea
                        id="configuration-content"
                        className="textarea textarea-bordered min-h-96 w-full font-mono text-sm"
                        value={content}
                        onChange={(event) => setContent(event.target.value)}
                        spellCheck={false}
                        disabled={isRequesting}
                    />
                </div>
            </section>

            <StatusModal
                isOpen={modalState.isOpen}
                message={modalState.message}
                onClose={() => setModalState((currentState) => ({ ...currentState, isOpen: false }))}
                title={modalState.title}
            />
        </div>
    );
}

export default ConfigurationTab;
